import { getGeneric } from "../../common/constants";
import { decodeAuthToken } from "../../common/util";

// In-memory cache for EMS lookups that rarely change — statuses, categories, the Assignee filter
// list, a ticket's assignable users. Every EMS page used to re-fetch these on each mount (filter
// bar, Create Ticket, View Ticket), so moving between pages repeated the same calls. Entries
// live for the browser session up to their TTL; a failed call isn't cached, so the next caller
// retries. Concurrent callers share one in-flight request. Expired entries are swept on every
// read, and the cache belongs to the signed-in user: some logout paths don't reload the page, so
// the first read for a different user (or none) starts from empty.
const cache = new Map();
let cacheOwner;

/** Who the cached answers were fetched for: the subject of the stored JWT, or null. */
const currentUser = () => {
    try {
        const token = localStorage.getItem("userToken");
        if (!token) return null;
        const claims = decodeAuthToken(token);
        return claims?.sub ?? claims?.id ?? null;
    } catch (e) {
        return null;
    }
};

export const CONFIG_TTL_MS = 30 * 60 * 1000;
export const ASSIGNEE_TTL_MS = 60 * 1000;

export const cachedGet = (url, ttlMs = CONFIG_TTL_MS) => {
    const user = currentUser();
    if (user !== cacheOwner) {
        cache.clear();
        cacheOwner = user;
    }
    const now = Date.now();
    cache.forEach((entry, key) => { if (entry.expiresAt <= now) cache.delete(key); });
    const hit = cache.get(url);
    if (hit) return hit.promise;
    const promise = getGeneric(url).catch((err) => {
        if (cache.get(url)?.promise === promise) cache.delete(url);
        throw err;
    });
    cache.set(url, { expiresAt: now + ttlMs, promise });
    return promise;
};

/** Drop cached entries whose URL contains `fragment` — e.g. after reassigning a ticket. */
export const invalidateCached = (fragment) => {
    [...cache.keys()].filter((url) => url.includes(fragment)).forEach((url) => cache.delete(url));
};
